import React from 'react';
import { Truck, Sparkles } from 'lucide-react';
import { formatCurrency } from '../../../lib/formatters';
import { useCoreContext } from '../../../lib/coreContext';

interface FreeDeliveryProgressBarProps {
  subtotal: number;
}

const TIER_FREE_DELIVERY_MINIMUMS: Record<string, number> = {
  SILVER: 50.0,
  GOLD: 40.0,
  PLATINUM: 30.0
};

export const FreeDeliveryProgressBar: React.FC<FreeDeliveryProgressBarProps> = ({ subtotal }) => {
  const core = useCoreContext();

  const rawTier = String(core.currentUser?.tier || 'COMMON').toUpperCase();
  const isEligibleTier = rawTier === 'SILVER' || rawTier === 'GOLD' || rawTier === 'PLATINUM';

  // For COMMON, BRONZE, and guests: free delivery is not an active tier benefit.
  // Display accurate membership perk information without claiming an active waiver.
  if (!isEligibleTier) {
    return (
      <div className="rounded-xl border border-white/10 bg-slate-900/60 p-3 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 text-slate-300">
          <Truck className="w-4 h-4 text-slate-400 shrink-0" />
          <span className="text-[11px] font-medium leading-snug">
            Members unlock Free Delivery on orders of $50+ (Silver), $40+ (Gold), $30+ (Platinum).
          </span>
        </div>
      </div>
    );
  }

  const threshold = TIER_FREE_DELIVERY_MINIMUMS[rawTier];
  const shortfall = Math.max(0, threshold - subtotal);
  const progressPercent = Math.min(100, Math.round((subtotal / threshold) * 100));
  const isUnlocked = shortfall <= 0;

  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/60 p-3.5 space-y-2.5">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5 font-black uppercase tracking-wider text-slate-200">
          <Truck className={`w-4 h-4 ${isUnlocked ? 'text-ninpo-lime' : 'text-amber-400'}`} />
          {isUnlocked ? (
            <span className="text-ninpo-lime flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> Free Delivery Unlocked!
            </span>
          ) : (
            <span>{rawTier} Free Delivery</span>
          )}
        </div>
        <span className="font-bold text-[11px] text-slate-400">
          {isUnlocked ? '100% Waived' : `Add ${formatCurrency(shortfall)} more`}
        </span>
      </div>

      {/* Progress Bar Container */}
      <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden relative">
        <div
          className={`h-full transition-all duration-500 rounded-full ${
            isUnlocked
              ? 'bg-gradient-to-r from-emerald-400 to-ninpo-lime'
              : 'bg-gradient-to-r from-amber-500 to-emerald-400'
          }`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <p className="text-[11px] text-slate-400 leading-snug">
        {isUnlocked ? (
          <span className="text-slate-200">
            Grocery margin covers fulfillment: your {rawTier} delivery fee is waived completely.
          </span>
        ) : (
          <span>
            Orders of <strong>{formatCurrency(threshold)}+</strong> qualify for 100% free delivery as a {rawTier} Member.
          </span>
        )}
      </p>
    </div>
  );
};

export default FreeDeliveryProgressBar;
